import { PerformanceReviewQuarter } from '@/common/graphql';

/** A Performance Review period: a year and its half (Q1/Q2 or Q3/Q4). */
export interface PerformanceReviewPeriodValue {
  year: number;
  quarter: PerformanceReviewQuarter;
}

const QUARTER_LABELS: Record<PerformanceReviewQuarter, string> = {
  [PerformanceReviewQuarter.Q1Q2]: 'Q1/Q2',
  [PerformanceReviewQuarter.Q3Q4]: 'Q3/Q4',
};

export const toPeriodKey = ({ year, quarter }: PerformanceReviewPeriodValue): string => `${year}-${quarter}`;

export const parsePeriodKey = (key: string | null | undefined): PerformanceReviewPeriodValue | null => {
  const match = /^(\d{4})-(Q1_Q2|Q3_Q4)$/.exec(key ?? '');
  if (!match) return null;
  return { year: Number(match[1]), quarter: match[2] as PerformanceReviewQuarter };
};

export const periodOf = (date: Date): PerformanceReviewPeriodValue => ({
  year: date.getFullYear(),
  quarter: date.getMonth() < 6 ? PerformanceReviewQuarter.Q1Q2 : PerformanceReviewQuarter.Q3Q4,
});

/** Halves counted from year 0, so that consecutive periods differ by 1. */
const ordinalOf = ({ year, quarter }: PerformanceReviewPeriodValue): number =>
  year * 2 + (quarter === PerformanceReviewQuarter.Q3Q4 ? 1 : 0);

/**
 * Every period from the one containing [from] to the one containing [to], newest first. Empty when [from] is after [to].
 * Q1/Q2 is January to June and Q3/Q4 is July to December.
 */
export const buildPeriodRange = (from: Date, to: Date): PerformanceReviewPeriodValue[] => {
  const first = ordinalOf(periodOf(from));
  const last = ordinalOf(periodOf(to));
  return Array.from({ length: Math.max(0, last - first + 1) }, (_, index) => {
    const ordinal = last - index;
    return {
      year: Math.floor(ordinal / 2),
      quarter: ordinal % 2 === 1 ? PerformanceReviewQuarter.Q3Q4 : PerformanceReviewQuarter.Q1Q2,
    };
  });
};

export const periodLabel = ({ year, quarter }: PerformanceReviewPeriodValue): string =>
  `${year} ${QUARTER_LABELS[quarter]}`;
